import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Eye, EyeOff, Mail } from 'lucide-react';

const AdminLogin = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1);
  const [resetMessage, setResetMessage] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetMessage('');

    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      
      if (response.ok) {
        setResetStep(2);
        setResetMessage('Ein Code wurde an Ihre E-Mail gesendet (falls registriert).');
      }
    } catch (err) {
      setResetMessage('Fehler beim Senden des Codes');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetMessage('');

    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: resetEmail, 
          code: resetCode, 
          new_password: newPassword 
        })
      });

      if (response.ok) {
        setResetMessage('Passwort erfolgreich geändert! Sie können sich jetzt einloggen.');
        setTimeout(() => {
          setShowResetForm(false);
          setResetStep(1);
          setResetEmail('');
          setResetCode('');
          setNewPassword('');
        }, 2000);
      } else {
        const data = await response.json();
        setResetMessage(data.detail || 'Fehler beim Zurücksetzen');
      }
    } catch (err) {
      setResetMessage('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setLoading(false);
    }
  };

  if (showResetForm) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f7f5' }}>
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
          <h1 className="text-2xl font-bold mb-6 text-center" style={{ color: '#1B4D3E' }}>
            Passwort zurücksetzen
          </h1>

          {resetStep === 1 ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B4D3E' }}>
                  E-Mail Adresse
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                    style={{ borderColor: '#1B4D3E', '--tw-ring-color': '#1B4D3E' }}
                    placeholder="ihre@email.de"
                    required
                  />
                </div>
              </div>

              {resetMessage && (
                <p className="text-sm text-green-600">{resetMessage}</p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-3"
                style={{ backgroundColor: '#1B4D3E' }}
              >
                {loading ? 'Senden...' : 'Code anfordern'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B4D3E' }}>
                  Bestätigungscode
                </label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 text-center text-2xl tracking-widest"
                  style={{ borderColor: '#1B4D3E' }}
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B4D3E' }}>
                  Neues Passwort
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#1B4D3E' }}
                  placeholder="Neues Passwort"
                  required
                />
              </div>

              {resetMessage && (
                <p className={`text-sm ${resetMessage.includes('erfolgreich') ? 'text-green-600' : 'text-red-600'}`}>
                  {resetMessage}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-3"
                style={{ backgroundColor: '#1B4D3E' }}
              >
                {loading ? 'Überprüfen...' : 'Passwort ändern'}
              </Button>
            </form>
          )}

          <button
            onClick={() => {
              setShowResetForm(false);
              setResetStep(1);
              setResetMessage('');
            }}
            className="w-full mt-4 text-sm underline"
            style={{ color: '#1B4D3E' }}
          >
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f7f5' }}>
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>
            Admin Login
          </h1>
          <p className="text-gray-500 mt-2">Zahnarztpraxis Muresan-Richter</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1B4D3E' }}>
              Benutzername
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
              style={{ borderColor: '#1B4D3E' }}
              placeholder="Benutzername"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1B4D3E' }}>
              Passwort
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 pr-12"
                style={{ borderColor: '#1B4D3E' }}
                placeholder="Passwort"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                ) : (
                  <Eye className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full py-3"
            style={{ backgroundColor: '#1B4D3E' }}
          >
            {loading ? 'Einloggen...' : 'Einloggen'}
          </Button>
        </form>

        <button
          onClick={() => setShowResetForm(true)}
          className="w-full mt-4 text-sm underline"
          style={{ color: '#1B4D3E' }}
        >
          Passwort vergessen?
        </button>
      </div>
    </div>
  );
};

export default AdminLogin;
